import { Cue } from "../../Interfaces/Cue/Cue";
import { CustomField } from "../../Interfaces/CustomField/CustomField";
import "./PrintableCueSheet.css";

interface PrintableCueSheetProps {
  title: string;
  date: Date;
  cues: Cue[];
  fields: CustomField[];
}

function fmtTime(iso: string) {
  return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true });
}

function fmtDuration(startIso: string, endIso: string) {
  const mins = Math.round((new Date(endIso).getTime() - new Date(startIso).getTime()) / 60000);
  if (mins <= 0) return "—";
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60); const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

/**
 * Print-only run-of-show sheet. Hidden on screen; revealed by the global
 * print stylesheet (index.css) when the user prints or saves to PDF.
 */
export function PrintableCueSheet({ title, date, cues, fields }: PrintableCueSheetProps) {
  const sorted = [...cues].sort((a, b) => a.cueNumber - b.cueNumber);
  return (
    <div className="print-sheet" aria-hidden="true">
      <div className="print-head">
        <div>
          <h1 className="print-title">{title || "Cue Sheet"}</h1>
          <div className="print-date">
            {date.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
          </div>
        </div>
        <div className="print-brand">LIVECUE</div>
      </div>

      <table className="print-table">
        <thead>
          <tr>
            <th className="print-col-num">#</th>
            <th className="print-col-time">Start</th>
            <th className="print-col-time">End</th>
            <th className="print-col-dur">Dur</th>
            <th className="print-col-title">Cue</th>
            {fields.map((f) => <th key={f.id}>{f.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {sorted.map((cue) => (
            <tr key={cue.id}>
              <td className="print-col-num">{cue.cueNumber}</td>
              <td className="print-col-time">{fmtTime(cue.startTime)}</td>
              <td className="print-col-time">{fmtTime(cue.endTime)}</td>
              <td className="print-col-dur">{fmtDuration(cue.startTime, cue.endTime)}</td>
              <td className="print-col-title">{cue.title || "—"}</td>
              {fields.map((f) => <td key={f.id}>{cue.fieldValues[f.id] || ""}</td>)}
            </tr>
          ))}
          {sorted.length === 0 && (
            <tr><td colSpan={5 + fields.length} className="print-empty">No cues</td></tr>
          )}
        </tbody>
      </table>

      <div className="print-foot">
        Generated by LiveCue · live-cue.com · {sorted.length} cue{sorted.length !== 1 ? "s" : ""}
      </div>
    </div>
  );
}
